import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type ThemeMode = "system" | "light" | "dark";

export interface BookingColors {
  paid: string;
  unpaid: string;
  deposit: string;
  cancelled: string;
}

interface SettingsState {
  fontScale: number;
  themeMode: ThemeMode;
  bookingColors: BookingColors;
}

const storageKey = "rentpilot_settings";

const fallbackState: SettingsState = {
  fontScale: 0,
  themeMode: "system",
  bookingColors: {
    paid: "#16a34a",
    unpaid: "#7c3aed",
    deposit: "#f59e0b",
    cancelled: "#64748b",
  },
};

const clampFontScale = (value: unknown): number => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return fallbackState.fontScale;
  }

  if (value < -2) {
    return -2;
  }

  if (value > 4) {
    return 4;
  }

  return value;
};

const normalizeThemeMode = (value: unknown): ThemeMode => {
  if (value === "system" || value === "light" || value === "dark") {
    return value;
  }

  return fallbackState.themeMode;
};

const normalizeBookingColors = (value: unknown): BookingColors => {
  if (!value || typeof value !== "object") {
    return fallbackState.bookingColors;
  }

  const incoming = value as Partial<BookingColors>;

  return {
    paid: incoming.paid || fallbackState.bookingColors.paid,
    unpaid: incoming.unpaid || fallbackState.bookingColors.unpaid,
    deposit: incoming.deposit || fallbackState.bookingColors.deposit,
    cancelled: incoming.cancelled || fallbackState.bookingColors.cancelled,
  };
};

const canUseLocalStorage = () => {
  return typeof window !== "undefined" && Boolean(window.localStorage);
};

const loadStoredSettings = (): SettingsState => {
  if (!canUseLocalStorage()) {
    return fallbackState;
  }

  try {
    const stored = window.localStorage.getItem(storageKey);

    if (!stored) {
      return fallbackState;
    }

    const parsed = JSON.parse(stored) as Partial<SettingsState>;

    return {
      fontScale: clampFontScale(parsed.fontScale),
      themeMode: normalizeThemeMode(parsed.themeMode),
      bookingColors: normalizeBookingColors(parsed.bookingColors),
    };
  } catch {
    window.localStorage.removeItem(storageKey);
    return fallbackState;
  }
};

const saveSettings = (state: SettingsState) => {
  if (!canUseLocalStorage()) {
    return;
  }

  window.localStorage.setItem(
    storageKey,
    JSON.stringify({
      fontScale: state.fontScale,
      themeMode: state.themeMode,
      bookingColors: state.bookingColors,
    }),
  );
};

const initialState: SettingsState = loadStoredSettings();

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    setFontScale: (state, action: PayloadAction<number>) => {
      state.fontScale = clampFontScale(action.payload);
      saveSettings(state);
    },
    setThemeMode: (state, action: PayloadAction<ThemeMode>) => {
      state.themeMode = normalizeThemeMode(action.payload);
      saveSettings(state);
    },
    setBookingColor: (
      state,
      action: PayloadAction<{ status: keyof BookingColors; color: string }>,
    ) => {
      state.bookingColors[action.payload.status] = action.payload.color;
      saveSettings(state);
    },
    resetSettings: () => {
      saveSettings(fallbackState);
      return fallbackState;
    },
  },
});

export const { setFontScale, setThemeMode, setBookingColor, resetSettings } =
  settingsSlice.actions;

export default settingsSlice.reducer;
